import type {
  FruitPricesByProduct,
  FruitProductId,
  FruitShopV2State,
} from "@cloudnest/fruit-shop-v2-engine";

export type FruitShopReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload: { readonly state: FruitShopV2State };
    }
  | {
      readonly eventType: "SyncViewerState";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    }
  | {
      readonly eventType: "ReadyForPriceSubmission";
      readonly userId: string;
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "SubmitPrices";
      readonly userId: string;
      readonly payload: { readonly prices: FruitPricesByProduct };
    }
  | {
      readonly eventType: "UseSecret";
      readonly userId: string;
      readonly payload: { readonly productId: FruitProductId };
    }
  | {
      readonly eventType: "UseChange";
      readonly userId: string;
      readonly payload: {
        readonly fromProductId: FruitProductId;
        readonly toProductId: FruitProductId;
      };
    }
  | {
      readonly eventType: "RevealRound";
      readonly userId: string;
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "ConfirmRoundResult";
      readonly userId: string;
      readonly payload?: Readonly<Record<string, never>>;
    }
  | {
      readonly eventType: "StartExtensionRound";
      readonly userId: string;
      readonly payload?: Readonly<Record<string, never>>;
    };

export type FruitShopReplayEventType = FruitShopReplayEvent["eventType"];

export interface FruitShopEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: Record<string, unknown>;
}

export const FRUIT_SHOP_REPLAY_EVENT_TYPES = [
  "init",
  "SyncViewerState",
  "ReadyForPriceSubmission",
  "SubmitPrices",
  "UseSecret",
  "UseChange",
  "RevealRound",
  "ConfirmRoundResult",
  "StartExtensionRound",
] as const satisfies readonly FruitShopReplayEventType[];

const FRUIT_SHOP_HOST_ONLY_EVENT_TYPES: ReadonlySet<FruitShopReplayEventType> =
  new Set(["init", "RevealRound", "StartExtensionRound"]);

export function isFruitShopHostOnlyReplayEvent(
  eventType: FruitShopReplayEventType,
): boolean {
  return FRUIT_SHOP_HOST_ONLY_EVENT_TYPES.has(eventType);
}

export function normalizeFruitShopReplayEventType(
  eventType: string,
): FruitShopReplayEventType {
  switch (eventType) {
    case "init":
    case "SyncViewerState":
    case "ReadyForPriceSubmission":
    case "SubmitPrices":
    case "UseSecret":
    case "UseChange":
    case "RevealRound":
    case "ConfirmRoundResult":
    case "StartExtensionRound":
      return eventType;
    case "HostSync":
      return "RevealRound";
    default:
      throw new Error(`Unsupported Fruit Shop event type: ${eventType}`);
  }
}

function requireUserId(event: FruitShopEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error(`Fruit Shop replay event is missing userId`);
}

export function toFruitShopReplayEvent(
  event: FruitShopEventInput,
): FruitShopReplayEvent {
  const eventType = normalizeFruitShopReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};

  switch (eventType) {
    case "init":
      return {
        eventType,
        userId,
        payload: payload as { readonly state: FruitShopV2State },
      };
    case "SyncViewerState":
      return {
        eventType,
        userId,
        payload: payload as { readonly actionId?: string },
      };
    case "ReadyForPriceSubmission":
    case "RevealRound":
    case "ConfirmRoundResult":
    case "StartExtensionRound":
      return { eventType, userId };
    case "SubmitPrices":
      return {
        eventType,
        userId,
        payload: payload as { readonly prices: FruitPricesByProduct },
      };
    case "UseSecret":
      return {
        eventType,
        userId,
        payload: payload as { readonly productId: FruitProductId },
      };
    case "UseChange":
      return {
        eventType,
        userId,
        payload: payload as {
          readonly fromProductId: FruitProductId;
          readonly toProductId: FruitProductId;
        },
      };
  }
}
